import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import axios from 'axios';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Issue } from 'validata';
import { FullscreenPage } from '../../components/fullscreen-page/index.js';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import { useSetTitle } from '../../hooks/use-title.js';
import { ErrorPage } from '../error/index.js';
import type { SetPasswordData, ViewProps } from './view.js';
import { SetPasswordView } from './view.js';

export const SetPasswordPage: FC<{ view?: FC<ViewProps<SetPasswordData>> }> = ({
  view: View = SetPasswordView,
}) => {
  // TODO log out user

  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SetPasswordData | Error | undefined>({
    confirmPassword: '',
    password: '',
  });
  const [issues, setIssues] = useState<Map<string, Issue[]>>(
    new Map<string, Issue[]>(),
  );
  useSetTitle('Set Password');

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const save = useCallback(() => {
    if (!token || data === undefined || data instanceof Error) return;

    const dataIssues = new Map<string, Issue[]>();

    const passwordCheck = {
      length: data.password.length,
      lowercase: data.password.match(/[a-z]/g)?.length,
      numbers: data.password.match(/\d/g)?.length,
      special: data.password.match(/[^a-zA-Z0-9]/g)?.length,
      uppercase: data.password.match(/[A-Z]/g)?.length,
    };
    const passwordIssues: Issue[] = [];
    if (passwordCheck.length < 8) {
      passwordIssues.push(
        Issue.forPath(['password'], data.password, 'Length must be 8 or more'),
      );
    }
    if (!passwordCheck.numbers) {
      passwordIssues.push(
        Issue.forPath(['password'], data.password, 'Must contain numbers'),
      );
    }
    if (!passwordCheck.lowercase) {
      passwordIssues.push(
        Issue.forPath(
          ['password'],
          data.password,
          'Must contain lower case letters',
        ),
      );
    }
    if (!passwordCheck.uppercase) {
      passwordIssues.push(
        Issue.forPath(
          ['password'],
          data.password,
          'Must contain upper case letters',
        ),
      );
    }
    if (!passwordCheck.special) {
      passwordIssues.push(
        Issue.forPath(
          ['password'],
          data.password,
          'Must contain special characters (e.g. "!@#$%^&*(){},.")',
        ),
      );
    }

    if (passwordIssues.length) {
      dataIssues.set('password', passwordIssues);
    }

    if (data.password !== data.confirmPassword) {
      dataIssues.set('confirmPassword', [
        Issue.forPath(
          ['confirmPassword'],
          data.confirmPassword,
          'password-mismatch',
        ),
      ]);
    }

    if (dataIssues.size > 0) {
      setIssues(dataIssues);
      return;
    }

    setLoading(true);
    axios
      .post(`/api/v1/set-password/${encodeURIComponent(token)}`, {
        password: data.password,
      })
      .then(() => {
        setLoading(false);
        navigate('/', { replace: true });
        return;
      })
      .catch((error: AxiosError<{ issues: Issue[] }>) => {
        console.log(error);
        Sentry.captureException(error);
        setLoading(false);
        if (error.response && error.response.status === 400) {
          if ('issues' in error.response.data) {
            const issues = error.response.data.issues;
            const indexed = issues.reduce((acc, issue) => {
              const path = issue.path.map((part) => String(part)).join('.');
              const array = acc.get(path) || [];
              array.push(issue);
              return acc.set(path, array);
            }, new Map<string, Issue[]>());
            setIssues(indexed);
            return;
          }
        }
        setData(error);
      });
  }, [token, data]);

  if (data === undefined || loading) return <MiddleSpinner />;

  if (data instanceof Error) return <ErrorPage error={data} />;

  const patchData = <
    K extends keyof SetPasswordData,
    V extends SetPasswordData[K],
  >(
    key: K,
    value: V,
  ): void => {
    setData({
      ...data,
      [key]: value,
    });
  };

  return (
    <FullscreenPage>
      <View data={data} issues={issues} patchData={patchData} save={save} />
    </FullscreenPage>
  );
};
